import React from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import types from '~/modules/customFieldTypes';
import User from '../../../models/User';
import Translate from '../../../components/Translate/Translate';
import { translate } from '~/modules/helpers/lang';
import { getLengthMessage } from '~/modules/helpers/messageHelpers';

const requiredFieldMessage = <Translate message="This field is required" />;
const emailMessage = <Translate message="Must be a valid email" />;
const mustMatchPasswordMessage = <Translate message="Must match password" />;

const missingUpperCaseMessage = upper => (
    <Translate
        message="Must contain at least {upper} uppercase letters"
        values={{
            upper
        }}
    />
);
const missingLowerCaseMessage = lower => (
    <Translate
        message="Must contain at least {lower} lowercase letters"
        values={{
            lower
        }}
    />
);
const missingDigitOrSpecialCharMessage = special => (
    <Translate
        message="Must contain at least {special} digits or special characters"
        values={{
            special
        }}
    />
);
const missingDigitMessage = () => (
    <Translate message="Must contain at least 1 digit" />
);

const missingSpecialCharMessage = () => (
    <Translate message="Must contain at least 1 special character" />
);

const legacyProfileMessage = (
    <Translate message="Used for legacy compatibility" />
);

//RN: don't change id & name. these are used in UserEditableFields.jsx and AddUserModal.jsx
const getUserFields = (
    length = 10,
    upper = 1,
    lower = 1,
    special = 2,
    custom = false
) => {
    //RN: don't change id & name. these are used in UserEditableFields.jsx and AddUserModal.jsx
    const addUserModalFields = [
        {
            id: 'name',
            name: 'name',
            label: translate('Name'),
            required: true,
            type: types.TEXT,
            validation: Yup.string()
                .required(requiredFieldMessage)
                .max(
                    50,
                    getLengthMessage({
                        limit: 50,
                        type: 'max'
                    })
                )
        },
        {
            id: 'surname',
            name: 'surname',
            label: translate('Surname'),
            required: true,
            type: types.TEXT,
            validation: Yup.string()
                .required(requiredFieldMessage)
                .max(
                    50,
                    getLengthMessage({
                        limit: 50,
                        type: 'max'
                    })
                )
        },
        {
            id: 'role',
            name: 'role',
            label: translate('Profile'),
            required: true,
            type: types.MULTILIST,
            validation: Yup.object().required(requiredFieldMessage),
            options: [],
            disableIf: id => {
                //rn: disable if is in edit mode and user hasn't permission to change
                if (!!id) {
                    if (CURRENT_USER.isSuperUser == 1) return false;
                    return !User.can('profile_management');
                }
                return false;
            }
        },
        {
            id: 'username',
            name: 'username',
            label: translate('Username'),
            required: true,
            type: types.TEXT,
            validation: Yup.string()
                .required(requiredFieldMessage)
                .max(
                    50,
                    getLengthMessage({
                        limit: 50,
                        type: 'max'
                    })
                ),
            disableIf: id => {
                //rn: disable if is in edit mode, in creation has to be editable
                return !!id;
            }
        },
        {
            id: 'mail',
            name: 'mail',
            label: translate('Mail'),
            required: true,
            type: types.EMAIL,
            validation: Yup.string()
                .required(requiredFieldMessage)
                .email(emailMessage)
        },
        /*{
            id: 'automatically_generate_password',
            name: 'automatically_generate_password',
            label: (
                <Translate message="Automatically generate password"
                />
            ),
            type: types.CHECKBOX,
            isSwitch: true
        },*/
        {
            id: 'password',
            name: 'password',
            label: translate('Password'),
            type: types.PASSWORD,
            visibleIf: values => !values['automatically_generate_password'],
            required: true,
            validation: Yup.string().when('automatically_generate_password', {
                is: true,
                then: Yup.string(),
                otherwise: Yup.string()
                    .required(requiredFieldMessage)
                    .min(
                        length,
                        getLengthMessage({
                            limit: length,
                            type: 'min'
                        })
                    )
                    .max(
                        40,
                        getLengthMessage({
                            limit: 40,
                            type: 'max'
                        })
                    )
                    .test(
                        'min_uppercase',
                        missingUpperCaseMessage(upper),
                        val => val?.replace(/[^A-Z]/g, '').length >= upper
                    )
                    .test(
                        'min_lowercase',
                        missingLowerCaseMessage(lower),
                        val => val?.replace(/[^a-z]/g, '').length >= lower
                    )
                    .test(
                        'min_digits_symbols',
                        missingDigitOrSpecialCharMessage(special),
                        val => {
                            return custom
                                ? val?.replace(/[a-zA-Z]/g, '').length >=
                                      special
                                : true;
                        }
                    )
                    .test('min_digits', missingDigitMessage(), val => {
                        return custom
                            ? true
                            : val?.replace(/[^0-9]/g, '').length >= 1;
                    })
                    .test('min_symbols', missingSpecialCharMessage(), val => {
                        return custom
                            ? true
                            : val?.replace(/[0-9a-zA-Z]/g, '').length >= 1;
                    })
            }),
            hint: (
                <>
                    <Translate
                        message="Password must be at least {length} characters in length and must meet the following requirements:"
                        values={{ length }}
                    />
                    <ul>
                        <li>
                            <Translate
                                message="{upper} uppercase letters"
                                values={{ upper }}
                            />
                        </li>
                        <li>
                            <Translate
                                message="{lower} lowercase letters"
                                values={{ lower }}
                            />
                        </li>
                        {custom ? (
                            <li>
                                <Translate
                                    message="{special} numbers or special characters"
                                    values={{ special }}
                                />
                            </li>
                        ) : (
                            <>
                                <li>
                                    <Translate message="1 number" />
                                </li>
                                <li>
                                    <Translate message="1 special character" />
                                </li>
                            </>
                        )}
                    </ul>
                </>
            )
        },
        {
            id: 'confirm_password',
            name: 'confirm_password',
            label: translate('Confirm password'),
            required: true,
            equalTo: 'password',
            type: types.PASSWORD,
            validation: Yup.string()
                .when('automatically_generate_password', {
                    is: true,
                    then: Yup.string(),
                    otherwise: Yup.string().required(requiredFieldMessage)
                })
                // mm: must match password value
                .oneOf([Yup.ref('password')], mustMatchPasswordMessage),
            visibleIf: values => !values['automatically_generate_password']
        },
        {
            id: 'timezone',
            name: 'timezone',
            label: translate('Timezone'),
            required: true,
            type: types.LIST,
            validation: Yup.object().required(requiredFieldMessage),
            // mm: options are retrieved asynchronously and added during rendering
            options: []
        },
        {
            id: 'telephone',
            name: 'telephone',
            label: translate('Telephone'),
            type: types.TEXT
        },
        {
            id: 'daylight_saving_time',
            name: 'daylight_saving_time',
            label: translate('Daylight saving time'),
            type: types.CHECKBOX,
            isSwitch: true
        } /*
        {
            id: 'can_use_ldap',
            name: 'can_use_ldap',
            label: translate('Use LDAP/AD for authentication'),
            type: types.CHECKBOX,
            isSwitch: true
        },*/,
        {
            id: 'global_search',
            name: 'global_search',
            label: translate('Global search'),
            type: types.CHECKBOX,
            isSwitch: true,
            disableIf: () => {
                return CURRENT_USER.isSuperUser !== 1;
            },
            hint:
                CURRENT_USER.isSuperUser !== 1 ? (
                    <Translate message="Only Super User can change this field" />
                ) : null
        },
        {
            id: 'additional_info',
            name: 'additional_info',
            label: translate('Description'),
            type: types.TEXTAREA
        }
    ];
    return addUserModalFields;
};

const userEditableFields = getUserFields()
    .filter(field => {
        return (
            field.id !== 'automatically_generate_password' &&
            field.id !== 'password' &&
            field.id !== 'confirm_password' &&
            // vo: do not show daylight saving time switch in one platform version
            (field.id !== 'daylight_saving_time' ||
                !SETTINGS.USING_ONE_PLATFORM) &&
            field.id !== 'global_search'
        );
    })
    //rn: if field id is equal to addUserModalFields field id, when modal is open, switch component are updated in
    // userDetail and not in modal.
    .map(field => {
        return {
            ...field,
            id: 'user_' + field.id
        };
    });

const optionType = PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string
});

const userFieldTypes = PropTypes.arrayOf(
    PropTypes.shape({
        name: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
            .isRequired,
        type: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            .isRequired,
        isSwitch: PropTypes.bool,
        options: PropTypes.arrayOf(optionType),
        /**
         * Yup schema
         */
        validation: PropTypes.object,
        required: PropTypes.bool,
        visibleIf: PropTypes.func,
        hint: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
    })
);

export {
    getUserFields,
    userEditableFields,
    userFieldTypes,
    optionType,
    requiredFieldMessage,
    emailMessage
};
